import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Skeleton,
  Grid,
  Card,
  CardContent,
  Container,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  VideoCall as VideoCallIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
} from "@mui/icons-material";
import { sweetStatusAlert } from "../../../components/common/SweetAlert";
import { ZoomMeetingService } from "../../../service/manage/zoom_meeting/zoom_meeting.service";
import CreateZoomMeeting from "./components/CreateMeeting";
import MeetingList from "./components/MeetingList";
import "./ManageZoomMeetings.css";
import LoadingSkeleton from "./components/LoadingSkeleton";

const StatCard = ({ icon, title, value, className }) => (
  <div className={`stat-card ${className}`}>
    <div className="stat-icon">{icon}</div>
    <div className="stat-content">
      <div className="stat-value">{value}</div>
      <div className="stat-title">{title}</div>
    </div>
  </div>
);

// const LoadingSkeleton = () => (
//   <Box className="loading-container">
//     <Grid container spacing={3}>
//       {[...Array(3)].map((_, i) => (
//         <Grid item xs={12} sm={6} md={4} key={i}>
//           <Skeleton variant="rectangular" height={140} className="skeleton-card" />
//         </Grid>
//       ))}
//     </Grid>
//     <Skeleton variant="rectangular" height={400} className="skeleton-content" />
//   </Box>
// );

export default function ManageZoomMeetings({ state }) {
  const {
    mid,
    lg_user_id,
    lg_user_table_id,
    privilege_add,
    privilege_edit,
    privilege_view,
    privilege_delete,
  } = state || {};

  const canCreateMeeting = privilege_add && privilege_edit;

  const [meetingList, setMeetingList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mid) fetchZoomMeetings();
  }, [mid]);

  const fetchZoomMeetings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await ZoomMeetingService.getZoomEventsByNodeMid(mid);
      if (res.success) {
        setMeetingList(res.data || []);
      } else {
        throw new Error(res.message || "Failed to fetch meetings");
      }
    } catch (err) {
      setError(err.message);
      sweetStatusAlert("Error", "Failed to load meetings", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMeeting = async (data) => {
    try {
      setIsProcessing(true);
      const payload = {
        ...data,
        node_mid: mid,
        create_user_id: lg_user_id,
        create_user_table_id: lg_user_table_id,
      };
      const res = await ZoomMeetingService.addZoomEvent(payload);
      if (res.success) {
        setMeetingList((prev) => [res.data, ...prev]);
        setModalOpen(false);
        sweetStatusAlert(
          "Success",
          "Meeting scheduled successfully",
          "success"
        );
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      sweetStatusAlert("Error", err.message, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditMeeting = async (id, update) => {
    try {
      setIsProcessing(true);
      const res = await ZoomMeetingService.updateZoomEvent(id, update);
      if (res.success) {
        setMeetingList((prev) =>
          prev.map((m) => (m.meeting_id === id ? res.data : m))
        );
        setModalOpen(false);
        setEditingMeeting(null);
        sweetStatusAlert("Success", "Meeting updated successfully", "success");
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      sweetStatusAlert("Error", err.message, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteMeeting = async (id) => {
    try {
      setIsProcessing(true);
      const res = await ZoomMeetingService.deleteZoomEvent(id);
      if (res.success) {
        setMeetingList((prev) => prev.filter((m) => m.meeting_id !== id));
        sweetStatusAlert("Success", "Meeting deleted successfully", "success");
      } else {
        throw new Error(res.message);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const getStats = () => {
    const now = new Date();
    const today = now.toDateString();
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return {
      total: meetingList.length,
      today: meetingList.filter(
        (m) => new Date(m.startdatetime).toDateString() === today
      ).length,
      thisWeek: meetingList.filter((m) => {
        const d = new Date(m.startdatetime);
        return d >= startOfWeek && d <= endOfWeek;
      }).length,
    };
  };

  const stats = getStats();

  const handleEditClick = (meeting) => {
    setEditingMeeting(meeting);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingMeeting(null);
  };

  return (
    <div className="zoom-container">
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Zoom Meetings</h1>
          {(privilege_edit || privilege_delete) && (
            <p className="page-subtitle">
              Manage and schedule your virtual meetings
            </p>
          )}
        </div>

        {canCreateMeeting && (
          <button
            className={`schedule-btn ${isProcessing ? "disabled" : ""}`}
            onClick={() => setModalOpen(true)}
            disabled={isProcessing}
          >
            <AddIcon />
            Schedule Meeting
          </button>
        )}
      </div>

      {!isLoading && (
        <div className="stats-container">
          <StatCard
            icon={<VideoCallIcon />}
            title="Total Meetings"
            value={stats.total}
            className="stat-primary"
          />
          <StatCard
            icon={<TodayIcon />}
            title="Today's Meetings"
            value={stats.today}
            className="stat-success"
          />
          <StatCard
            icon={<WeekIcon />}
            title="This Week"
            value={stats.thisWeek}
            className="stat-info"
          />
        </div>
      )}

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          {error}
        </div>
      )}

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <MeetingList
          meetings={meetingList}
          onEdit={handleEditClick}
          onDelete={handleDeleteMeeting}
          onRefresh={fetchZoomMeetings}
          privileges={{
            view: privilege_view,
            edit: privilege_edit,
            delete: privilege_delete,
          }}
          isProcessing={isProcessing}
        />
      )}

      <CreateZoomMeeting
        open={modalOpen}
        onClose={handleModalClose}
        onCreate={handleCreateMeeting}
        onUpdate={handleEditMeeting}
        editingMeeting={editingMeeting}
        isProcessing={isProcessing}
      />
    </div>
  );
}
